import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, localSeoFor, relatedServiceLinks } from "../controller/global";
import { Link } from "react-router";
import { submitLead } from "../controller/leads";
import { Images } from "../controller/Images";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// Title and description come from the shared localSeo map so this page's
// Delhi NCR targeting stays in step with the other local landing pages.
const { title: localTitle, description: localDescription } = localSeoFor(
  "/custom-software-development"
);

export const customSoftwareSEO = {
  title: localTitle,
  description: localDescription,
  keywords:
    "Custom Software Development, Enterprise Software Development, Bespoke Software Development, AI-Integrated Software, SaaS Platform Development, Software Development Company India",
  canonical: "https://inqmisetechnologies.com/custom-software-development",
  og: {
    title: localTitle,
    description: "Bespoke, enterprise-grade software tailored to your business — full-stack development, AI integration, and legacy modernization. Based in Delhi NCR.",
    url: "https://inqmisetechnologies.com/custom-software-development",
    type: "website",
    image: "https://inqmisetechnologies.com/logo.png",
  },
  twitter: {
    card: "summary_large_image",
    title: localTitle,
    description: "Enterprise-grade bespoke software built for your business, with AI integration and modern tech stacks. Delhi NCR based.",
    image: "https://inqmisetechnologies.com/logo.png",
  },
};

const SERVICES = [
  { title: "Full-Stack Development", desc: "End-to-end custom software development using React, Node.js, Next.js, and modern frameworks." },
  { title: "Enterprise Software Solutions", desc: "Scalable, secure enterprise applications with microservices, cloud integration, and DevOps." },
  { title: "AI-Integrated Software", desc: "Build intelligent applications with machine learning, generative AI, and data analytics." },
  { title: "Mobile App Development", desc: "Cross-platform mobile solutions using React Native, Flutter, and native technologies." },
  { title: "SaaS Platform Development", desc: "Build subscription-based platforms with multi-tenancy, payment integration, and analytics." },
  { title: "Legacy System Modernization", desc: "Refactor and modernize existing systems while maintaining continuity and reducing risk." },
];

const ENGAGEMENT_MODELS = [
  {
    name: "Fixed-Scope Project",
    tagline: "For a clearly defined deliverable",
    features: ["Fixed price & timeline", "Detailed spec sign-off upfront", "Milestone-based payments", "Ideal for well-scoped builds"],
  },
  {
    name: "Dedicated Team",
    tagline: "For ongoing, evolving product work",
    highlight: true,
    features: ["Your own full-stack squad", "Flexes with changing priorities", "Direct daily collaboration", "Best for long-term partnerships"],
  },
  {
    name: "MVP Sprint",
    tagline: "For validating an idea fast",
    features: ["4-6 week fixed sprint", "Launch-ready MVP scope", "Built-in user feedback loop", "Ideal for early-stage products"],
  },
];

const PROCESS_STEPS = [
  { step: "01", title: "Discovery & Planning", desc: "Deep dive into your requirements, goals, and technical needs." },
  { step: "02", title: "Architecture Design", desc: "Scalable, secure, and maintainable software architecture." },
  { step: "03", title: "Development", desc: "Agile development with clean code and regular communication." },
  { step: "04", title: "Testing & QA", desc: "Unit, integration, and user acceptance testing." },
  { step: "05", title: "Deployment", desc: "Seamless production rollout with minimal downtime." },
  { step: "06", title: "Maintenance & Support", desc: "Ongoing monitoring, updates, and optimization." },
];

const TECH_STACK = [
  { cat: "Frontend", techs: ["React", "Next.js", "Vue.js", "Angular", "TypeScript", "Tailwind CSS"] },
  { cat: "Backend", techs: ["Node.js", "Python", ".NET", "Go", "Java", "GraphQL"] },
  { cat: "Database", techs: ["MongoDB", "MySQL", "PostgreSQL", "Firebase"] },
  { cat: "Infrastructure", techs: ["AWS", "Azure", "Docker", "Kubernetes", "CI/CD", "GitHub Actions"] },
];

const TRACK_RECORD = [
  { metric: "Projects Delivered", value: "150+" },
  { metric: "Avg. Budget Adherence", value: "98%" },
  { metric: "On-time Delivery", value: "95%+" },
  { metric: "Client Satisfaction", value: "4.9/5" },
];

const INDUSTRIES = [
  "Healthcare & MedTech", "Finance & FinTech", "E-Commerce & Retail",
  "Logistics & Supply Chain", "Legal & Professional Services", "Manufacturing",
];

const CASE_STUDIES = [
  { org: "Regional Retail Chain", result: "40% faster checkout", detail: "Modernized a legacy POS system into a cloud-native platform without a single day of store downtime." },
  { org: "B2B SaaS Startup", result: "MVP in 5 weeks", detail: "Took a product concept to a paying-customer-ready MVP using our sprint engagement model." },
  { org: "Logistics Operator", result: "30% fewer manual errors", detail: "Built a custom dispatch and tracking system integrated directly into their existing ERP." },
];

const FAQS = [
  {
    q: "What is custom software development and how is it different from off-the-shelf software?",
    a: "Custom software development builds an application specifically for your workflows and constraints, rather than forcing your business to adapt to a generic, one-size-fits-all product.",
  },
  {
    q: "Which engagement model should we choose?",
    a: "Fixed-Scope suits a well-defined deliverable, Dedicated Team suits ongoing evolving product work, and MVP Sprint suits validating a new idea quickly — we'll help you pick the right fit in a discovery call.",
  },
  {
    q: "Can you modernize our existing legacy system instead of a full rebuild?",
    a: "Yes. Legacy system modernization is one of our core enterprise software development services — we refactor incrementally to reduce risk and preserve business continuity.",
  },
  {
    q: "Do you build AI-integrated software?",
    a: "Yes, AI-integrated software is a standard part of our custom software development offering, including machine learning features, generative AI, and data analytics.",
  },
  {
    q: "How long does a typical custom software development project take?",
    a: "Timelines range from a 4-6 week MVP sprint to 4-6+ months for a full enterprise platform, depending on scope and integration complexity.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

const CustomSoftwareDevelopment = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = customSoftwareSEO.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = customSoftwareSEO.description;
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = customSoftwareSEO.keywords;
    document.head.appendChild(keywords);

    const ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    ogTitle.content = customSoftwareSEO.og.title;
    document.head.appendChild(ogTitle);

    const ogDesc = document.createElement("meta");
    ogDesc.setAttribute("property", "og:description");
    ogDesc.content = customSoftwareSEO.og.description;
    document.head.appendChild(ogDesc);

    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = customSoftwareSEO.og.url;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    ogType.content = customSoftwareSEO.og.type;
    document.head.appendChild(ogType);

    const ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    ogImage.content = customSoftwareSEO.og.image;
    document.head.appendChild(ogImage);

    const twitterCard = document.createElement("meta");
    twitterCard.name = "twitter:card";
    twitterCard.content = customSoftwareSEO.twitter.card;
    document.head.appendChild(twitterCard);

    const twitterTitle = document.createElement("meta");
    twitterTitle.name = "twitter:title";
    twitterTitle.content = customSoftwareSEO.twitter.title;
    document.head.appendChild(twitterTitle);

    const twitterDesc = document.createElement("meta");
    twitterDesc.name = "twitter:description";
    twitterDesc.content = customSoftwareSEO.twitter.description;
    document.head.appendChild(twitterDesc);

    const twitterImage = document.createElement("meta");
    twitterImage.name = "twitter:image";
    twitterImage.content = customSoftwareSEO.twitter.image;
    document.head.appendChild(twitterImage);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", customSoftwareSEO.canonical);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Custom Software Development",
      "description": customSoftwareSEO.description,
      "url": customSoftwareSEO.canonical,
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "availableLanguage": ["en-IN"],
      "serviceType": "Custom Software Development",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": "Custom Software Development", "item": customSoftwareSEO.canonical },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Please tell us about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);
  const faqLeft = FAQS.slice(0, Math.ceil(FAQS.length / 2));
  const faqRight = FAQS.slice(Math.ceil(FAQS.length / 2));

  const renderFaqItem = (item, i) => {
    const isOpen = openFaq === i;
    const panelId = `faq-panel-${i}`;
    const buttonId = `faq-button-${i}`;
    return (
      <div className="ea-faq-row" key={item.q}>
        <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
          <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>
            {item.q}<span>{isOpen ? "−" : "+"}</span>
          </button>
        </h3>
        <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
      </div>
    );
  };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO — same split panel shape as the other AI/service pages */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Bespoke enterprise software · built for your business</p>
          <h1>Custom software development that fits your business.</h1>
          <p className="ea-lede">
            Enterprise-grade bespoke solutions tailored to your requirements — full-stack development, AI integration, and legacy modernization, delivered by a team that owns the outcome.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your custom software project">
              Start your project ↗
            </button>
            <a className="ea-text-link" href="#services">See what we build</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / TRACK RECORD <b>VERIFIED</b></div>
          <h2>Built by a team that owns the outcome.</h2>
          {TRACK_RECORD.slice(0, 2).map((stat) => (
            <div className="ea-query" key={stat.metric}><small>{stat.metric.toUpperCase()}</small>{stat.value}</div>
          ))}
          <div className="ea-flow"><b>Scope</b><i>→</i><b>Build</b><i>→</i><b>Ship</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* SERVICES — numbered 2-column list */}
      <section className="ea-wrap ea-section" id="services">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What we build</div>
            <h2>Custom development,<br />end to end.</h2>
          </div>
          <p>Comprehensive custom software solutions from ideation through deployment and maintenance.</p>
        </Reveal>
        <div className="cs-list">
          {SERVICES.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="cs-list-row" key={item.title}>
              <span className="cs-list-num">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ENGAGEMENT MODELS — tiered offer cards */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">How we work together</div>
              <h2>Choose the model<br />that fits your team.</h2>
            </div>
            <p>Three ways to engage, depending on how defined your scope is and how long we'll be working together.</p>
          </Reveal>
          <div className="cs-tiers">
            {ENGAGEMENT_MODELS.map((tier, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} key={tier.name} className={`cs-tier ${tier.highlight ? "cs-tier-highlight" : ""}`}>
                {tier.highlight && <span className="cs-tier-badge">Most popular</span>}
                <h3>{tier.name}</h3>
                <p className="cs-tagline">{tier.tagline}</p>
                <ul>
                  {tier.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
                <button onClick={openContactModal} className={`ea-button ${tier.highlight ? "ea-primary" : ""}`} style={tier.highlight ? {} : { border: "1px solid var(--ink)" }}>
                  Discuss this model
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS — horizontal rail */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">A proven methodology</div>
            <h2>Six stages,<br />one accountable delivery.</h2>
          </div>
          <p>The same process whether it's a 5-week MVP or a multi-quarter enterprise build.</p>
        </Reveal>
        <div className="cs-rail">
          {PROCESS_STEPS.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="cs-rail-step" key={item.step}>
              <span className="cs-rail-step-num">{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TECH STACK */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Under the hood</div>
            <h2>A modern, proven<br />technology stack.</h2>
          </div>
          <p>Chosen for the project, not for the résumé — the same rigor we apply to every architecture decision.</p>
        </Reveal>
        <div className="cs-tech-grid">
          {TECH_STACK.map((col, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} key={col.cat} className="cs-tech-card">
              <span className="cs-tech-card-num">{String(i + 1).padStart(2, "0")}</span>
              <h3>{col.cat}</h3>
              <div className="cs-tags-row">
                {col.techs.map((tech) => <span key={tech} className="cs-tag">{tech}</span>)}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPECIALISED BUILDS — keyword links to the pages that go deeper */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Specialized builds</div>
            <h2>Custom software,<br />by type of project.</h2>
          </div>
          <p>Most custom software projects are one of these. Each has its own page on how we approach it.</p>
        </Reveal>
        <div className="cs-tags-row">
          {relatedServiceLinks["/custom-software-development"].map((s) => (
            <Link key={s.path} to={s.path} className="cs-tag" style={{ fontSize: "var(--t-xs)", padding: "12px 20px", textDecoration: "none" }}>{s.title} →</Link>
          ))}
        </div>
      </section>

      {/* INDUSTRIES — simple tag row */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where we've delivered</div>
            <h2>Regulated and<br />high-growth alike.</h2>
          </div>
          <p>Enterprise software development experience across industries with very different constraints.</p>
        </Reveal>
        <div className="cs-tags-row">
          {INDUSTRIES.map((industry) => (
            <span key={industry} className="cs-tag" style={{ fontSize: "var(--t-xs)", padding: "12px 20px" }}>{industry}</span>
          ))}
        </div>
      </section>

      {/* CASE STUDIES — compact result cards */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Selected outcomes</div>
            <h2>Representative results,<br />not cherry-picked outliers.</h2>
          </div>
          <p>A sample of recent custom software development engagements.</p>
        </Reveal>
        <div className="cs-results-grid">
          {CASE_STUDIES.map((item, i) => (
            <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="cs-result-card" key={item.org}>
              <strong>{item.result}</strong>
              <b>{item.org}</b>
              <p>{item.detail}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ — two columns instead of a single centered accordion */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head" style={{ display: "block", textAlign: "center" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Common questions</div>
            <h2>Custom software development FAQs</h2>
            <p style={{ margin: "15px auto 40px", maxWidth: 500 }}>Short answers to the questions we are usually asked before an engagement begins.</p>
          </Reveal>
          <div className="cs-faq-split">
            <div>{faqLeft.map((item, i) => renderFaqItem(item, i))}</div>
            <div>{faqRight.map((item, i) => renderFaqItem(item, i + faqLeft.length))}</div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">Ready when you are</div>
            <h2 id="contact-heading">Bring us the software you actually need.</h2>
            <p>Tell us about your requirements and our team will get back to you within one business day.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>FREE CONSULTATION</span>
            <h3>Build with more certainty.</h3>
            <p>Tell us what you're trying to build and we'll help you scope it.</p>

            <form onSubmit={handleContactSubmit} aria-label="Custom software development project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="cust-soft-fullname">Full Name</label>
                <input
                  id="cust-soft-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "cust-soft-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="cust-soft-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="cust-soft-phone">Phone Number (optional)</label>
                  <input
                    id="cust-soft-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "cust-soft-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="cust-soft-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="cust-soft-email">Email</label>
                  <input
                    id="cust-soft-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "cust-soft-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="cust-soft-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="cust-soft-subject">Subject</label>
                <input
                  id="cust-soft-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., MVP for our new marketplace"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "cust-soft-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="cust-soft-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="cust-soft-description">Project Details</label>
                <textarea
                  id="cust-soft-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "cust-soft-description-error" : undefined}
                />
                {contactErrors.description && <p id="cust-soft-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Get your free consultation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CustomSoftwareDevelopment;
